import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  UserCredential
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "./config";

export type AllowedUserRole = "ADMIN" | "CLIENT" | "USER" | "PUBLIC";

export interface SignUpResult {
  uid: string;
  email: string;
  name: string;
  companyName: string;
}

const ADMIN_EMAILS = [
  "admin@verveailabs.com",
  "manju@verveailabs.com",
  "manjushukla89@gmail.com",
  "rds087@gmail.com"
];

/**
 * Normalizes user role string to typed AllowedUserRole
 */
export function normalizeUserRole(rawRole?: string | null, email?: string | null): AllowedUserRole {
  const normEmail = (email || "").trim().toLowerCase();
  if (ADMIN_EMAILS.includes(normEmail)) {
    return "ADMIN";
  }

  const role = (rawRole || "").trim().toUpperCase();
  if (role === "SUPER_ADMIN" || role === "ADMIN" || role === "CA") {
    return "ADMIN";
  }
  if (role === "CLIENT") {
    return "CLIENT";
  }
  if (role === "USER") {
    return "USER";
  }
  return "PUBLIC";
}

/**
 * Determines a user's role from token, users collection, or clients collection
 */
export async function resolveUserRole(user: User): Promise<{
  role: AllowedUserRole;
  clientId: string;
  companyName?: string;
  isAuthorized: boolean;
}> {
  const normEmail = (user.email || "").trim().toLowerCase();

  // 1. Check Admin Email List
  if (ADMIN_EMAILS.includes(normEmail)) {
    return {
      role: "ADMIN",
      clientId: user.uid,
      companyName: "Verve AI Labs (Admin Console)",
      isAuthorized: true
    };
  }

  // 2. Check Custom Claims in Auth Token
  try {
    const tokenResult = await user.getIdTokenResult();
    const claimRole = (tokenResult.claims.role as string || "").toUpperCase();
    if (claimRole === "ADMIN" || claimRole === "SUPER_ADMIN" || claimRole === "CA") {
      return {
        role: "ADMIN",
        clientId: user.uid,
        companyName: "Verve AI Labs (Admin Console)",
        isAuthorized: true
      };
    }
    if (claimRole === "CLIENT") {
      return {
        role: "CLIENT",
        clientId: (tokenResult.claims.assignedClientId as string) || user.uid,
        isAuthorized: true
      };
    }
  } catch (err) {
    console.warn("Could not retrieve custom claims:", err);
  }

  // 3. Check /users/{uid} document in Firestore
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      const normRole = normalizeUserRole(data.role, user.email);
      if (normRole === "ADMIN") {
        return {
          role: "ADMIN",
          clientId: user.uid,
          companyName: data.companyName || "Admin Console",
          isAuthorized: true
        };
      }
      if (normRole === "CLIENT") {
        return {
          role: "CLIENT",
          clientId: data.assignedClientId || user.uid,
          companyName: data.companyName || data.name,
          isAuthorized: true
        };
      }
      if (normRole === "USER") {
        // Standard registered user without CLIENT or ADMIN privileges
        return {
          role: "USER",
          clientId: user.uid,
          companyName: data.companyName || data.name,
          isAuthorized: false
        };
      }
    }
  } catch (err) {
    console.warn("Could not read users doc:", err);
  }

  // 4. Check if /clients/{uid} exists directly
  try {
    const clientDocRef = doc(db, "clients", user.uid);
    const clientSnap = await getDoc(clientDocRef);
    if (clientSnap.exists()) {
      const clientData = clientSnap.data();
      return {
        role: "CLIENT",
        clientId: user.uid,
        companyName: clientData.name || clientData.companyName,
        isAuthorized: true
      };
    }
  } catch (err) {
    console.warn("Could not read direct client doc:", err);
  }

  // 5. Query /clients where email matches user.email
  if (user.email) {
    try {
      const clientsCol = collection(db, "clients");
      const q = query(clientsCol, where("email", "==", user.email));
      const matchSnap = await getDocs(q);
      if (!matchSnap.empty) {
        const clientDoc = matchSnap.docs[0];
        const clientData = clientDoc.data();
        return {
          role: "CLIENT",
          clientId: clientDoc.id,
          companyName: clientData.name || clientData.companyName,
          isAuthorized: true
        };
      }
    } catch (err) {
      console.warn("Could not query client by email:", err);
    }
  }

  // Default: Standard registered user without CLIENT or ADMIN privileges
  return {
    role: "USER",
    clientId: user.uid,
    isAuthorized: false
  };
}

export const signUpWithEmail = async (
  email: string,
  pass: string,
  displayName?: string,
  companyName?: string
): Promise<SignUpResult> => {
  const cred = await createUserWithEmailAndPassword(auth, email, pass);
  const cleanEmail = (cred.user.email || email).trim().toLowerCase();
  const resolvedName = (displayName || "").trim() || cleanEmail.split("@")[0];
  const resolvedCompany = (companyName || "").trim();

  // Create /users/{uid} document with USER role in central database
  try {
    const userDocRef = doc(db, "users", cred.user.uid);
    await setDoc(userDocRef, {
      uid: cred.user.uid,
      email: cleanEmail,
      name: resolvedName,
      displayName: resolvedName,
      company: resolvedCompany,
      companyName: resolvedCompany,
      role: "USER", // Standard registered user role in central database
      status: "active",
      gstAccessStatus: "PENDING_ADMIN_APPROVAL",
      sourcePlatform: "gst.verveai.co",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      onboardingComplete: false
    }, { merge: true });
  } catch (e) {
    console.error("Failed to write user profile to central users table:", e);
    throw e;
  }

  // Immediately sign out to prevent auto-directing into the platform before admin approval
  try {
    await signOut(auth);
  } catch (signOutErr) {
    console.warn("Sign out following registration:", signOutErr);
  }

  return {
    uid: cred.user.uid,
    email: cleanEmail,
    name: resolvedName,
    companyName: resolvedCompany,
  };
};

export const signInWithEmail = (email: string, pass: string): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, pass);
};

export const signInWithGoogle = async (): Promise<UserCredential> => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account"
  });
  const cred = await signInWithPopup(auth, provider);

  // If user doc doesn't exist, create it with USER role in central users table
  try {
    const userDocRef = doc(db, "users", cred.user.uid);
    const snap = await getDoc(userDocRef);
    if (!snap.exists()) {
      const cleanEmail = (cred.user.email || "").toLowerCase();
      const isAutoAdmin = ADMIN_EMAILS.includes(cleanEmail);
      const displayName = cred.user.displayName || cleanEmail.split("@")[0] || "User";
      await setDoc(userDocRef, {
        uid: cred.user.uid,
        email: cleanEmail,
        name: displayName,
        displayName: displayName,
        role: isAutoAdmin ? "ADMIN" : "USER", // Standard users get USER role
        status: "active",
        gstAccessStatus: isAutoAdmin ? "APPROVED" : "PENDING_ADMIN_APPROVAL",
        sourcePlatform: "gst.verveai.co",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        onboardingComplete: false
      });
    }
  } catch (e) {
    console.warn("Could not sync Google sign-in user profile:", e);
  }

  return cred;
};

export const signOutUser = (): Promise<void> => {
  return signOut(auth);
};

export const sendResetPassword = async (email: string): Promise<void> => {
  const cleanEmail = (email || "").trim().toLowerCase();
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://gst.verveai.co";

  // 1. Primary: Dispatch enterprise branded HTML email via Verve AI Labs Custom Email Service
  try {
    const res = await fetch("https://admin.verveailabs.com/api/auth/send-custom-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type: "PASSWORD_RESET",
        email: cleanEmail,
        redirectUrl: `${currentOrigin}/`,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        return;
      }
    }
  } catch (customErr) {
    console.warn("Custom branded email service unavailable, falling back to Firebase Auth SDK:", customErr);
  }

  // 2. Fallback: Firebase Auth Client SDK with ActionCodeSettings redirecting to current origin
  const actionCodeSettings = {
    url: `${currentOrigin}/`,
    handleCodeInApp: false,
  };
  return sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
};

export const sendVerificationEmail = async (user: User): Promise<void> => {
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://gst.verveai.co";

  if (user.email) {
    try {
      const res = await fetch("https://admin.verveailabs.com/api/auth/send-custom-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "EMAIL_VERIFICATION",
          email: user.email.trim().toLowerCase(),
          recipientName: user.displayName || user.email.split("@")[0],
          redirectUrl: `${currentOrigin}/`,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) return;
      }
    } catch (err) {
      console.warn("Custom verification email failed, falling back to Firebase Auth SDK:", err);
    }
  }

  const actionCodeSettings = {
    url: `${currentOrigin}/`,
    handleCodeInApp: false,
  };
  return sendEmailVerification(user, actionCodeSettings);
};

export const onAuthStateListener = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// --- Comprehensive Firebase Authentication Error Mapper & Company Copy ---

export interface AuthErrorInfo {
  code: string;
  title: string;
  message: string;
  actionType?: "switch_to_signin" | "switch_to_register" | "forgot_password" | "contact_support" | "retry";
  actionLabel?: string;
  secondaryActionType?: "forgot_password" | "contact_support";
  secondaryActionLabel?: string;
}

/**
 * Maps raw Firebase authentication errors or error strings into structured,
 * company-standard messages conforming to Verve AI Labs design guidelines.
 */
export function mapAuthError(
  err: any,
  context?: { email?: string; mode?: "signin" | "register" | "forgot" }
): AuthErrorInfo {
  let code = (err?.code || "").trim().toLowerCase();
  const rawMessage = (err?.message || (typeof err === "string" ? err : "")).trim();

  // If code is not explicitly present, extract from error message string: "(auth/foo-bar)"
  if (!code && rawMessage) {
    const match = rawMessage.match(/\((auth\/[a-z0-9-]+)\)/i);
    if (match) {
      code = match[1].toLowerCase();
    }
  }

  const email = context?.email?.trim() || "";

  switch (code) {
    case "auth/email-already-in-use":
      return {
        code,
        title: "Email Already Registered",
        message: email
          ? `An account for ${email} already exists in the Verve AI Labs directory. Sign in to access your statutory GST workspace.`
          : "An account with this corporate email address already exists. Please sign in to access your workspace.",
        actionType: "switch_to_signin",
        actionLabel: "Switch to Sign In",
        secondaryActionType: "forgot_password",
        secondaryActionLabel: "Forgot password?",
      };

    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return {
        code,
        title: "Invalid Credentials",
        message:
          "The email address or password entered does not match our records. Please verify your credentials or reset your password.",
        actionType: "forgot_password",
        actionLabel: "Reset Password",
      };

    case "auth/weak-password":
      return {
        code,
        title: "Password Too Weak",
        message:
          "For compliance security, passwords must be at least 6 characters long and include a combination of letters and numbers.",
        actionType: "retry",
        actionLabel: "Try Again",
      };

    case "auth/invalid-email":
      return {
        code,
        title: "Invalid Email Address",
        message:
          "Please enter a valid corporate email format (e.g., yourname@company.com).",
        actionType: "retry",
        actionLabel: "Correct Email",
      };

    case "auth/user-disabled":
      return {
        code,
        title: "Account Disabled",
        message:
          "This account has been administratively disabled. Please contact Verve AI Labs compliance support (support@verveailabs.com).",
        actionType: "contact_support",
        actionLabel: "Contact Support",
      };

    case "auth/too-many-requests":
      return {
        code,
        title: "Security Lockout",
        message:
          "Access is temporarily paused due to multiple unsuccessful attempts. Please wait a few moments or reset your password.",
        actionType: "forgot_password",
        actionLabel: "Reset Password",
      };

    case "auth/popup-closed-by-user":
      return {
        code,
        title: "Google Sign-In Cancelled",
        message:
          "The Google authentication popup was closed before completing the sign-in sequence. Please try again.",
        actionType: "retry",
        actionLabel: "Try Google Sign-In Again",
      };

    case "auth/popup-blocked":
      return {
        code,
        title: "Popup Blocked by Browser",
        message:
          "Your browser blocked the Google authentication popup. Please allow popups for this portal and retry.",
        actionType: "retry",
        actionLabel: "Retry",
      };

    case "auth/unauthorized-domain":
      return {
        code,
        title: "Domain Not Authorized",
        message:
          "This domain is not registered in Firebase Authentication authorized domains. Please notify technical administration.",
        actionType: "contact_support",
        actionLabel: "Contact Admin",
      };

    case "auth/network-request-failed":
      return {
        code,
        title: "Network Connection Error",
        message:
          "Unable to communicate with the authentication server. Please check your internet connection and try again.",
        actionType: "retry",
        actionLabel: "Retry Connection",
      };

    case "auth/operation-not-allowed":
      return {
        code,
        title: "Sign-In Method Restricted",
        message:
          "This authentication provider is currently disabled for this workspace. Please use Google Workspace or contact support.",
        actionType: "contact_support",
        actionLabel: "Contact Support",
      };

    case "auth/requires-recent-login":
      return {
        code,
        title: "Recent Authentication Required",
        message:
          "This security action requires a fresh sign-in session. Please sign out and sign in again.",
        actionType: "switch_to_signin",
        actionLabel: "Sign In Again",
      };

    default: {
      // Strip technical Firebase SDK prefixes: "Firebase: Error (auth/...):"
      let clean = rawMessage
        .replace(/^Firebase:\s*/i, "")
        .replace(/Error\s*\([a-z0-9/-]+\):\s*/i, "")
        .replace(/\([a-z0-9/-]+\)\.?/i, "")
        .trim();

      if (!clean || clean.length < 3) {
        clean = "An unexpected authentication error occurred. Please try again.";
      }

      return {
        code: code || "auth/unknown",
        title: "Authentication Notice",
        message: clean,
        actionType: "retry",
        actionLabel: "Try Again",
      };
    }
  }
}

/**
 * Returns a human-friendly string message for backward compatibility.
 */
export function mapFirebaseAuthError(err: any): string {
  return mapAuthError(err).message;
}

